import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@sylviq/ui-react-web/components/ui/card";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { extensionDisplayMetadata } from "./ExtensionDocumentation";
import type { ExtensionUpdate } from "./extension-updates";

export function ExtensionsUpdatesPanel({
  updates,
  canManage,
  updatingInstallationId,
  onUpdate,
}: {
  updates: ExtensionUpdate[];
  canManage: boolean;
  updatingInstallationId: string | null;
  onUpdate: (update: ExtensionUpdate) => void;
}) {
  return (
    <section aria-labelledby="extension-updates-heading" className="space-y-4">
      <div>
        <h2 id="extension-updates-heading" className="text-lg font-semibold">
          Extensions needing updates
        </h2>
        <p className="text-sm text-[var(--muted-foreground)]">
          Installed extensions with a newer published release available. Updating
          applies the release to this organization; it does not install anything new.
        </p>
      </div>
      {updates.length === 0 ? (
        <EmptyState
          title="Everything is up to date"
          reason="No installed extension has a newer published release."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {updates.map((update) => {
            const displayName = `${update.installation.publisher}/${update.installation.key}`;
            const summary = extensionDisplayMetadata(update.latest.manifest).summary;
            const updating = updatingInstallationId === update.installation.id;
            return (
              <Card key={update.installation.id}>
                <CardHeader>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <CardTitle>{displayName}</CardTitle>
                    <Badge tone="warning">Update available</Badge>
                  </div>
                  <CardDescription>
                    Version {update.installation.version} → {update.latest.version}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 pt-3">
                  {summary ? (
                    <p className="text-sm">{summary}</p>
                  ) : null}
                  {canManage ? (
                    <Button
                      aria-label={`Update ${displayName} to version ${update.latest.version}`}
                      disabled={updatingInstallationId !== null}
                      onClick={() => onUpdate(update)}
                      size="sm"
                    >
                      {updating ? "Updating..." : `Update to ${update.latest.version}`}
                    </Button>
                  ) : (
                    <p className="text-sm text-[var(--muted-foreground)]">
                      Only the organization owner can update extensions.
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </section>
  );
}
